import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Tag } from '../types/tag';
import { updateTag } from '../api/tagApi';

function getPowerUps(data: Tag | null) {
    const allPowerUps = [
        { name: 'smart-refresh', enabled: true }, // Always enabled
        { name: 'layout-shift-protection', enabled: true }, // Always enabled
        { name: 'prebid', enabled: data?.prebid?.enabled || false },
        { name: 'prebid-user-sync', enabled: data?.prebid?.userSyncEnabled || false },
        { name: 'bot-detection', enabled: data?.botDetectionEnabled || false },
        { name: 'advanced-bot-detection', enabled: data?.botDetectionAdvancedEnabled || false },
        { name: 'custom-tag-domain', enabled: !!(data?.customDomain) },
        { name: 'disable-gam-tag', enabled: data?.disableGptAdServer || false },
        { name: 'priority-viewport-loading', enabled: false },
        { name: 'realtime-monitoring', enabled: false },
        { name: 'ppid', enabled: data?.ppidEnabled || false },
        { name: 'email-tracking', enabled: data?.emailTrackingEnabled || false },
        { name: 'consent-management', enabled: false },
        { name: 'adblock-detection', enabled: data?.adBlockEnabled || false },
        { name: 'viewability-tracking', enabled: false },
        { name: 'network-latency-monitor', enabled: false },
    ];

    return allPowerUps;
}

const predefinedGranularities = ['low', 'medium', 'high', 'auto', 'dense'];
function getPriceGranularity(granularity: any) {
    if (typeof granularity === 'string' && predefinedGranularities.includes(granularity)) {
        return granularity;
    }
    return 'custom';
}

export const useTagForm = (data: Tag | null, onSuccess?: (data: Tag) => void) => {
    const [loading, setLoading] = useState(false);
    const [initialFormState, setInitialFormState] = useState<string>('');
    const [formInitialized, setFormInitialized] = useState(false);

    const validationSchema = Yup.object({
        name: Yup.string().trim().required('O nome é obrigatório'),
        domain: Yup.string()
            .trim()
            .required('O domínio é obrigatório')
            .matches(
                /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,}$/,
                'Informe um hostname válido'
            ),
        customDomain: Yup.string()
            .notRequired()
            .test('is-subdomain', 'O domínio customizado deve ser um subdomínio do site principal', function (value) {
                if (!value) return true;
                const domain = this.parent.domain;
                if (!domain) return true;
                return value.endsWith('.' + domain);
            }),
        advertiserLabel: Yup.string().trim().notRequired(),
        advertiserLabelPosition: Yup.string().oneOf(['top', 'bottom'], 'Posição inválida').required(),
        pageContainerSize: Yup.string()
            .trim()
            .notRequired()
            .test('valid-container-size', 'Formato inválido. Use valores como: 1200 ou 1200px', function (value) {
                if (!value) return true;
                // Allow just numbers (like "1200") or numbers with "px" (like "1200px")
                const containerSizeRegex = /^(\d+(?:\.\d+)?)(?:px)?$/i;
                return containerSizeRegex.test(value);
            }),
        thirdPartyScripts: Yup.array().of(
            Yup.object({
                name: Yup.string().trim().required('Nome do script é obrigatório'),
                type: Yup.string().oneOf(['script', 'overwrite'], 'Tipo inválido').required('Tipo é obrigatório'),
                inputType: Yup.string().oneOf(['url', 'code'], 'Tipo de input inválido').required('Tipo de input é obrigatório'),
                enabled: Yup.boolean().notRequired(),
                deliveryPercentage: Yup.number()
                    .min(0, 'Porcentagem deve ser entre 0% e 100%')
                    .max(100, 'Porcentagem deve ser entre 0% e 100%')
                    .required('Porcentagem de entrega é obrigatória'),
                src: Yup.string().when('inputType', {
                    is: 'url',
                    then: (schema) => schema.url('URL inválida').required('URL é obrigatória quando tipo de input for URL'),
                    otherwise: (schema) => schema.notRequired()
                }),
                text: Yup.string().when('inputType', {
                    is: 'code',
                    then: (schema) => schema.trim().required('Código é obrigatório quando tipo de input for Código'),
                    otherwise: (schema) => schema.notRequired()
                }),
                format: Yup.string().when('type', {
                    is: 'overwrite',
                    then: (schema) => schema.trim().required('Formato é obrigatório para tipo overwrite'),
                    otherwise: (schema) => schema.notRequired()
                }),
                formatIndex: Yup.number().when('type', {
                    is: 'overwrite',
                    then: (schema) => schema.min(0, 'Índice deve ser maior ou igual a 0').required('Índice do formato é obrigatório para tipo overwrite'),
                    otherwise: (schema) => schema.notRequired()
                })
            })
        ),
        userSessionDurationHours: Yup.number()
            .min(1, 'Duração da sessão deve ser pelo menos 1 hora')
            .max(720, 'Duração da sessão deve ser no máximo 720 horas (30 dias)')
            .required('Duração da sessão é obrigatória'),
        adBlock: Yup.object({
            title: Yup.string().trim().max(100, 'Título deve ter no máximo 100 caracteres').notRequired(),
            message: Yup.string().trim().max(500, 'Mensagem deve ter no máximo 500 caracteres').notRequired(),
            reloadButton: Yup.string().trim().max(50, 'Texto do botão deve ter no máximo 50 caracteres').notRequired(),
            allowClose: Yup.boolean().notRequired()
        }),
        loadOnScroll: Yup.boolean().notRequired()
    });

    const getInitialValues = (tagData: Tag | null) => ({
        id: tagData ? tagData.id : '',
        name: tagData ? tagData.name : '',
        domain: tagData ? tagData.domain : '',
        customDomain: tagData ? tagData.customDomain : '',
        enabled: tagData ? tagData.enabled : true,
        debug: tagData ? tagData.debug : false,
        customCssText: tagData ? tagData.customCssText : '',
        placements: tagData?.placements?.map((placement: any) => {
            const customFields = placement.customFields && typeof placement.customFields === 'object'
                ? placement.customFields
                : null;
            const rewardedType = customFields?.rewardedType || placement.rewardedType || '';
            const rewardedConfig = customFields?.rewardedConfig || placement.rewardedConfig || null;

            // Remove rewarded fields from customFields before stringifying
            const cleanCustomFields = customFields
                ? (() => {
                    const { rewardedType: _rt, rewardedConfig: _rc, ...rest } = customFields;
                    return Object.keys(rest).length > 0 ? JSON.stringify(rest, null, 2) : null;
                })()
                : null;

            return {
                ...placement,
                // Convert bidders object to string for form handling
                bids: placement.bids && typeof placement.bids === 'object'
                    ? JSON.stringify(placement.bids, null, 2)
                    : placement.bids || null,
                attributes: placement.attributes && typeof placement.attributes === 'object'
                    ? JSON.stringify(placement.attributes, null, 2)
                    : placement.attributes || null,
                customFields: cleanCustomFields,
                // Rewarded fields at placement level for form handling
                rewardedType,
                rewardedConfig,
            };
        }) || [],
        loadOnScroll: tagData ? tagData.loadOnScroll : false,
        blockedPages: tagData?.blockedPages?.map((page: any) => ({
            ...page,
            enabled: page.enabled !== undefined ? page.enabled : true
        })) || [],
        thirdPartyScripts: tagData?.thirdPartyScripts || [],
        prebid: {
            enabled: tagData?.prebid?.enabled || false,
            timeout: tagData?.prebid?.timeout || 1500,
            failSafeTimeout: tagData?.prebid?.failSafeTimeout || 2500,
            priceGranularity: getPriceGranularity(tagData?.prebid?.priceGranularity) || 'dense',
            priceGranularityCustom: tagData?.prebid?.priceGranularityCustom ? JSON.stringify(tagData?.prebid?.priceGranularityCustom, null, 2) : '',
            userSyncEnabled: tagData?.prebid?.userSyncEnabled || false,
            minFloor: tagData?.prebid?.minFloor || 0.05,
        },
        userSessionDurationHours: tagData ? tagData.userSessionDurationHours : 12,
        advertiserLabel: tagData ? tagData.advertiserLabel : '',
        advertiserLabelPosition: tagData ? tagData.advertiserLabelPosition : 'top',
        minAdSpace: tagData ? tagData.minAdSpace : '',
        disableGptAdServer: tagData ? tagData.disableGptAdServer : false,
        customJavascriptText: tagData ? tagData.customJavascriptText : '',
        pageContainerSize: tagData ? tagData.pageContainerSize : '1200px',
        botDetectionEnabled: tagData ? tagData.botDetectionEnabled : false,
        botDetectionAdvancedEnabled: tagData ? tagData.botDetectionAdvancedEnabled : false,
        botDetectionThreshold: tagData ? tagData.botDetectionThreshold : 0.7,
        lazyLoad: tagData?.lazyLoad ? {
            fetchMarginPercent: tagData.lazyLoad.fetchMarginPercent ?? 200,
            renderMarginPercent: tagData.lazyLoad.renderMarginPercent ?? 100,
            mobileScaling: tagData.lazyLoad.mobileScaling ?? 2.0
        } : null,
        ppidEnabled: tagData?.ppidEnabled || false,
        emailTrackingEnabled: tagData?.emailTrackingEnabled || false,
        adBlockEnabled: tagData?.adBlockEnabled || false,
        analyticsEnabled: tagData?.analyticsEnabled || false,
        analyticsStandard: tagData?.analyticsStandard || false,
        analyticsAdvanced: tagData?.analyticsAdvanced || false,
        analyticsFull: tagData?.analyticsFull || false,
        fingerprintingEnabled: tagData?.fingerprintingEnabled || false,
        allowIframeAds: tagData?.allowIframeAds || false,
        adBlockConfig: {
            title: tagData?.adBlockConfig?.title || '',
            message: tagData?.adBlockConfig?.message || '',
            reloadText: tagData?.adBlockConfig?.reloadText || '',
            showBranding: tagData?.adBlockConfig?.showBranding !== undefined ? tagData.adBlockConfig.showBranding : true,
            allowClose: tagData?.adBlockConfig?.allowClose !== undefined ? tagData.adBlockConfig.allowClose : true
        },
        powerUps: getPowerUps(tagData),
    });

    const scrollToError = (fieldName: string) => {
        // Handle nested field names (e.g., 'prebid.enabled')
        const fieldSelector = `[name="${fieldName}"]`;
        const errorElement = document.querySelector(fieldSelector) as HTMLElement;

        if (errorElement) {
            // Scroll to the element with some offset
            const elementRect = errorElement.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const middle = absoluteElementTop - (window.innerHeight / 2);

            window.scrollTo({
                top: middle,
                behavior: 'smooth'
            });

            // Focus the element after scrolling
            setTimeout(() => {
                errorElement.focus();
            }, 300);
        }
    };

    const initialValues = getInitialValues(null); // Default empty values

    const handleForm = useFormik({
        enableReinitialize: false,
        initialValues,
        validationSchema,
        onSubmit: async (values: any) => {
            setLoading(true);

            try {
                if (values.prebid.priceGranularity === 'custom') {
                    values.prebid.priceGranularityCustom = values.prebid.priceGranularityCustom
                        ? JSON.parse(values.prebid.priceGranularityCustom)
                        : null;
                } else {
                    values.prebid.priceGranularityCustom = null;
                }
            } catch (error) {
                toast.error('JSON inválido em Price Granularity');
                setLoading(false);
                return;
            }

            // Validate rewarded format specific fields
            for (const placement of values.placements) {
                if (placement.name === 'REWARDED' && placement.provider === 'gpt' && placement.enabled) {
                    // Check if rewardedType is required and present
                    if (!placement.rewardedType) {
                        toast.error(`Tipo de recompensa é obrigatório para formato ${placement.displayName || placement.name}`);
                        setLoading(false);
                        return;
                    }

                    // Check if accessDuration is required for premium_article or paywall_removal
                    const requiresDuration = placement.rewardedType === 'premium_article' || placement.rewardedType === 'paywall_removal';
                    if (requiresDuration && (!placement.rewardedConfig || !placement.rewardedConfig.accessDuration)) {
                        toast.error(`Duração do acesso é obrigatória para tipo "${placement.rewardedType}" no formato ${placement.displayName || placement.name}`);
                        setLoading(false);
                        return;
                    }
                }
            }

            // Process placements - convert back to backend format
            values.placements = values.placements.map((placement: any) => {
                const result = {
                    ...placement,
                    attributes: placement.attributes ? JSON.parse(placement.attributes) : null,
                };

                // Handle bids JSON conversion
                if (placement.bids && typeof placement.bids === 'string') {
                    try {
                        result.bids = JSON.parse(placement.bids);
                    } catch (error) {
                        console.error('Invalid bids JSON:', placement.bids);
                        result.bids = null;
                    }
                }

                if (placement.attributes && typeof placement.attributes === 'string') {
                    try {
                        result.attributes = JSON.parse(placement.attributes);
                    } catch (error) {
                        console.error('Invalid attributes JSON:', placement.attributes);
                        result.attributes = null;
                    }
                }

                if (placement.customFields && typeof placement.customFields === 'string') {
                    try {
                        result.customFields = JSON.parse(placement.customFields);
                    } catch (error) {
                        console.error('Invalid customFields JSON:', placement.customFields);
                        result.customFields = null;
                    }
                }

                // Move rewarded fields back into customFields for persistence
                if (result.rewardedType || result.rewardedConfig) {
                    result.customFields = {
                        ...(result.customFields || {}),
                        ...(result.rewardedType ? { rewardedType: result.rewardedType } : {}),
                        ...(result.rewardedConfig ? { rewardedConfig: result.rewardedConfig } : {}),
                    };
                }
                delete result.rewardedType;
                delete result.rewardedConfig;

                if (result.provider === 'adp' && result.name === "custom" && String(result.id).startsWith('custom-')) {
                    delete result.id;
                }
                delete result.tagId;
                return result;
            });

            // Create a copy of values for API submission
            const apiValues = { ...values };
            
            // Process power ups for API
            if (Array.isArray(apiValues.powerUps)) {
                // Reset all power up related values in API copy
                apiValues.prebid.enabled = false;
                apiValues.prebid.userSyncEnabled = false;
                apiValues.botDetectionEnabled = false;
                apiValues.botDetectionAdvancedEnabled = false;
                apiValues.disableGptAdServer = false;
                apiValues.ppidEnabled = false;
                apiValues.emailTrackingEnabled = false;
                apiValues.adBlockEnabled = false;

                for (const powerUp of apiValues.powerUps) {
                    switch (powerUp.name) {
                        case 'smart-refresh': // Always enabled
                        case 'layout-shift-protection': // Always enabled
                        case 'priority-viewport-loading':
                        case 'realtime-monitoring':
                        case 'consent-management':
                        case 'viewability-tracking':
                        case 'network-latency-monitor':
                            break;
                        case 'prebid':
                            apiValues.prebid.enabled = powerUp.enabled;
                            break;
                        case 'prebid-user-sync':
                            apiValues.prebid.userSyncEnabled = powerUp.enabled;
                            break;
                        case 'bot-detection':
                            apiValues.botDetectionEnabled = powerUp.enabled;
                            break;
                        case 'advanced-bot-detection':
                            apiValues.botDetectionAdvancedEnabled = powerUp.enabled;
                            break;
                        case 'custom-tag-domain':
                            // Custom domain is handled by the customDomain field itself
                            // Power up is automatically enabled when customDomain has a value
                            break;
                        case 'disable-gam-tag':
                            apiValues.disableGptAdServer = powerUp.enabled;
                            break;
                        case 'ppid':
                            apiValues.ppidEnabled = powerUp.enabled;
                            break;
                        case 'email-tracking':
                            apiValues.emailTrackingEnabled = powerUp.enabled;
                            break;
                        case 'adblock-detection':
                            apiValues.adBlockEnabled = powerUp.enabled;
                            break;
                        default:
                            console.error(`Power Up desconhecido: ${powerUp.name}`);
                            break;
                    }
                }
            }

            // Remove fields that shouldn't be sent to API
            delete apiValues.powerUps;
            delete apiValues.id;

            try {
                if (data) {
                    const res = await updateTag(data.id, apiValues);
                    toast.success('TAG atualizada com sucesso!');
                    
                    // Update form values with the response data
                    const newFormValues = getInitialValues(res);
                    handleForm.setValues(newFormValues);
                    setInitialFormState(JSON.stringify(newFormValues));
                    
                    onSuccess?.(res);
                    return res;
                }
            } catch (error) {
                throw error;
            } finally {
                setLoading(false);
            }
        }
    });

    // Initialize form values when data changes
    useEffect(() => {
        // Initialize form with the actual data
        const newInitialValues = getInitialValues(data);

        // Set form values and update initial state tracking
        handleForm.setValues(newInitialValues);
        setInitialFormState(JSON.stringify(newInitialValues));
        setFormInitialized(true);
    }, [data?.id]);

    // Check if form has been modified
    const isDirty = initialFormState !== '' && initialFormState !== JSON.stringify(handleForm.values);

    // Enhanced handleSubmit that handles validation errors
    const enhancedHandleSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        // Trigger validation manually
        const errors = await handleForm.validateForm();
        handleForm.setTouched(
            Object.keys(handleForm.values).reduce((touched, key) => {
                touched[key] = true;
                return touched;
            }, {} as any)
        );

        if (Object.keys(errors).length > 0) {
            // There are validation errors
            Object.values(errors).forEach((errorMsg) => {
                if (typeof errorMsg === 'string') {
                    toast.error(errorMsg);
                } else if (typeof errorMsg === 'object' && errorMsg !== null) {
                    // Handle nested errors (e.g., prebid)
                    Object.values(errorMsg).forEach((nestedMsg) => {
                        if (typeof nestedMsg === 'string') {
                            toast.error(nestedMsg);
                        }
                    });
                }
            });
            const firstErrorField = Object.keys(errors)[0];
            scrollToError(firstErrorField);
            return false; // Return false to indicate validation failure
        }

        // No validation errors, proceed with submission
        try {
            await handleForm.handleSubmit(e);
            return true; // Return true to indicate success
        } catch (error) {
            return false; // Return false to indicate submission failure
        }
    };

    // Method to manually reset form to original data
    const resetForm = () => {
        const resetValues = getInitialValues(data);
        handleForm.setValues(resetValues);
        setInitialFormState(JSON.stringify(resetValues));
        handleForm.setTouched({});
    };

    return {
        handleForm: {
            ...handleForm,
            handleSubmit: enhancedHandleSubmit
        },
        loading,
        isDirty,
        resetForm,
        formInitialized,
    };
};